import { GRID, PORT, STAGE } from './portLayout';
import { SimpleLayout } from '../types';

export const makeTemplateForPorts = (count: number): SimpleLayout => {
  if (count <= 1) return make1();
  if (count === 2) return make2();
  if (count <= 8) return make8(count);
  if (count <= 16) return make16(count);
  if (count <= 24) return make24(count);
  return make48(count);
};

export const make1 = (): SimpleLayout => {
  const stage = STAGE.desktop1;
  const x = GRID * 3;
  const y = Math.round((stage.height - PORT.height) / 2 / GRID) * GRID;
  return {
    stage,
    ports: [
      { id: 'p-1', number: 1, kind: 'rj45' as const, x, y, flipped: false },
    ],
  };
};

export const make2 = (): SimpleLayout => {
  const stage = STAGE.desktop2;
  const y = Math.round((stage.height - PORT.height) / 2 / GRID) * GRID;
  return {
    stage,
    ports: [
      { id: 'p-1', number: 1, kind: 'rj45' as const, x: GRID * 3, y, flipped: false },
      { id: 'p-2', number: 2, kind: 'rj45' as const, x: GRID * 3 + PORT.width + GRID, y, flipped: false },
    ],
  };
};

export const make8 = (count = 8): SimpleLayout => {
  const stage = STAGE.desktop8;
  const y = Math.round((stage.height - PORT.height) / 2 / GRID) * GRID;
  return {
    stage,
    ports: Array.from({ length: count }).map((_, i) => ({
      id: `p-${i + 1}`,
      number: i + 1,
      kind: 'rj45' as const,
      x: GRID * 2 + i * (PORT.width + GRID),
      y,
      flipped: false,
    })),
  };
};

export const make16 = (count = 16): SimpleLayout => {
  const stage = STAGE.rack16;
  const perRow = Math.ceil(count / 2);
  const VSEP = 12; // px gap between rows
  const topY = Math.round((stage.height - (PORT.height * 2 + VSEP)) / 2);
  const botY = topY + PORT.height + VSEP;
  const portsTop = Array.from({ length: perRow }).map((_, i) => ({
    id: `p-${i + 1}`,
    number: i + 1,
    kind: 'rj45' as const,
    x: GRID * 3 + i * (PORT.width + GRID),
    y: topY,
    flipped: false,
  }));
  const portsBot = Array.from({ length: count - perRow }).map((_, i) => ({
    id: `p-${perRow + i + 1}`,
    number: perRow + i + 1,
    kind: 'rj45' as const,
    x: GRID * 3 + i * (PORT.width + GRID),
    y: botY,
    flipped: true,
  }));
  return { stage, ports: [...portsTop, ...portsBot] };
};

export const make24 = (count = 24): SimpleLayout => {
  const stage = STAGE.rack24;
  const perRow = Math.ceil(count / 2);
  const VSEP = 12; // px gap between rows
  const topY = Math.round((stage.height - (PORT.height * 2 + VSEP)) / 2);
  const botY = topY + PORT.height + VSEP;
  // compute startX so 24 ports fit within stage width
  const totalRowWidth = perRow * PORT.width + (perRow - 1) * GRID;
  const startX = Math.max(GRID * 2, Math.round((stage.width - totalRowWidth) / 2));
  const portsTop = Array.from({ length: perRow }).map((_, i) => ({
    id: `p-${i + 1}`,
    number: i + 1,
    kind: 'rj45' as const,
    x: startX + i * (PORT.width + GRID),
    y: topY,
    flipped: false,
  }));
  const portsBot = Array.from({ length: count - perRow }).map((_, i) => ({
    id: `p-${perRow + i + 1}`,
    number: perRow + i + 1,
    kind: 'rj45' as const,
    x: startX + i * (PORT.width + GRID),
    y: botY,
    flipped: true,
  }));
  return { stage, ports: [...portsTop, ...portsBot] };
};

export const make48 = (count = 48): SimpleLayout => {
  const stage = STAGE.rack48;
  const perRow = Math.ceil(count / 2);
  const VSEP = 12; // px gap between rows
  const topY = Math.round((stage.height - (PORT.height * 2 + VSEP)) / 2);
  const botY = topY + PORT.height + VSEP;
  // Align ports to the left like other templates
  const startX = GRID * 3;
  const portsTop = Array.from({ length: perRow }).map((_, i) => ({
    id: `p-${i + 1}`,
    number: i + 1,
    kind: 'rj45' as const,
    x: startX + i * (PORT.width + GRID),
    y: topY,
    flipped: false,
  }));
  const portsBot = Array.from({ length: count - perRow }).map((_, i) => ({
    id: `p-${perRow + i + 1}`,
    number: perRow + i + 1,
    kind: 'rj45' as const,
    x: startX + i * (PORT.width + GRID),
    y: botY,
    flipped: true,
  }));
  return { stage, ports: [...portsTop, ...portsBot] };
};
